import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useApiFetch } from "../api/apiClient";
import { colors } from "../utils/styles";
import { fmtDate } from "../utils/dateFormat";

const COLUMNS = [
  { key: "jurisdictionCode", label: "Jurisdiction Code", mono: true },
  { key: "jurisdictionName", label: "Jurisdiction Name" },
  { key: "userEmail", label: "User Email" },
  { key: "user", label: "User" },
  { key: "accessLevel", label: "Access Level" },
  { key: "approvedBy", label: "Approved By" },
  { key: "approvedDate", label: "Approved Date" }
];

export default function AdminAccessLevelsTest() {
  const apiFetch = useApiFetch();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("jurisdictionCode");
  const [sortDir, setSortDir] = useState("asc");

  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [editing, setEditing] = useState(null); // null = closed; { isNew, jurisdictionCode, userEmail, user }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/admin/access-levels-test");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(await res.json());
    } catch (err) {
      setError(err.message || "Failed to load access levels.");
    }
  }, [apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    apiFetch("/admin/access-levels-test/jurisdiction-options")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setJurisdictionOptions(Array.isArray(data) ? data : []))
      .catch(() => setJurisdictionOptions([]));
  }, [apiFetch]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    if (!sortBy) return rows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const aNull = av === null || av === undefined || av === "";
      const bNull = bv === null || bv === undefined || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }, [rows, sortBy, sortDir]);

  const pagedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const openAdd = () => {
    setFormError(null);
    setEditing({ isNew: true, jurisdictionCode: "", userEmail: "", user: "" });
  };

  const openEdit = (row) => {
    setFormError(null);
    setEditing({
      isNew: false,
      jurisdictionCode: row.jurisdictionCode,
      userEmail: row.userEmail,
      user: row.user || ""
    });
  };

  const closeDialog = () => {
    setEditing(null);
    setFormError(null);
  };

  const onSave = async () => {
    if (!editing) return;
    setSaving(true);
    setFormError(null);
    try {
      let url, options;
      if (editing.isNew) {
        if (!editing.jurisdictionCode || !editing.userEmail) {
          throw new Error("Jurisdiction and User Email are required.");
        }
        url = "/admin/access-levels-test";
        options = {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jurisdictionCode: editing.jurisdictionCode,
            userEmail: editing.userEmail.trim(),
            user: editing.user || null
          })
        };
      } else {
        const qs = `?jurisdictionCode=${encodeURIComponent(editing.jurisdictionCode)}&userEmail=${encodeURIComponent(editing.userEmail)}`;
        url = `/admin/access-levels-test${qs}`;
        options = {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user: editing.user || null })
        };
      }
      const res = await apiFetch(url, options);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setRows((prev) => {
        if (!prev) return [updated];
        if (editing.isNew) return [updated, ...prev];
        return prev.map((r) =>
          r.jurisdictionCode === updated.jurisdictionCode && r.userEmail === updated.userEmail ? updated : r
        );
      });
      closeDialog();
    } catch (err) {
      setFormError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const qs = `?jurisdictionCode=${encodeURIComponent(pendingDelete.jurisdictionCode)}&userEmail=${encodeURIComponent(pendingDelete.userEmail)}`;
      const res = await apiFetch(`/admin/access-levels-test${qs}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }
      setRows((prev) =>
        prev
          ? prev.filter(
              (r) =>
                !(
                  r.jurisdictionCode === pendingDelete.jurisdictionCode &&
                  r.userEmail === pendingDelete.userEmail
                )
            )
          : prev
      );
      setPendingDelete(null);
    } catch (err) {
      setError(err.message || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  const selectedJurisdiction = editing
    ? jurisdictionOptions.find((j) => j.jurisdictionCode === editing.jurisdictionCode) || null
    : null;

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          PT Access to Online Preliminary Form
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          Add
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> PT staff can complete the online preliminary form on an agency's behalf.
          To complete the form, first add the jurisdiction code and the PT staff's email and name on this page.
          Next, using the Email Blast page, email the online form to the selected PT staff.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!error && rows === null && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {rows && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>No access level entries yet.</Box>
      )}

      {rows && rows.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead
                sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: colors.darkGray,
                    color: "white",
                    fontWeight: 700,
                    whiteSpace: "nowrap"
                  },
                  "& .MuiTableSortLabel-root": { color: "white !important" },
                  "& .MuiTableSortLabel-root.Mui-active": { color: "white !important" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" }
                }}
              >
                <TableRow>
                  {COLUMNS.map((c) => (
                    <TableCell key={c.key} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : "asc"}
                        onClick={() => toggleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow key={`${r.jurisdictionCode}|${r.userEmail}`} hover>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.jurisdictionCode}</TableCell>
                    <TableCell>{r.jurisdictionName || "—"}</TableCell>
                    <TableCell>{r.userEmail}</TableCell>
                    <TableCell>{r.user || "—"}</TableCell>
                    <TableCell>{r.accessLevel || "—"}</TableCell>
                    <TableCell>{r.approvedBy || "—"}</TableCell>
                    <TableCell>{fmtDate(r.approvedDate)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit" arrow>
                        <IconButton size="small" onClick={() => openEdit(r)} aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete" arrow>
                        <IconButton size="small" onClick={() => setPendingDelete(r)} aria-label="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={sortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      <Dialog open={editing !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.isNew ? "Add Access Level" : "Edit Access Level"}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={jurisdictionOptions}
              value={selectedJurisdiction}
              onChange={(_, v) =>
                setEditing((prev) => prev && { ...prev, jurisdictionCode: v?.jurisdictionCode || "" })
              }
              getOptionLabel={(o) =>
                o?.jurisdictionName
                  ? `${o.jurisdictionName} (${o.jurisdictionCode})`
                  : o?.jurisdictionCode || ""
              }
              isOptionEqualToValue={(o, v) => o.jurisdictionCode === v.jurisdictionCode}
              disabled={!editing?.isNew}
              renderInput={(params) => <TextField {...params} label="Jurisdiction" required size="small" />}
            />
            <TextField
              label="User Email"
              type="email"
              required
              size="small"
              value={editing?.userEmail || ""}
              onChange={(e) => setEditing((prev) => prev && { ...prev, userEmail: e.target.value })}
              disabled={!editing?.isNew}
            />
            <TextField
              label="User (display name)"
              size="small"
              value={editing?.user || ""}
              onChange={(e) => setEditing((prev) => prev && { ...prev, user: e.target.value })}
            />
            {editing?.isNew && (
              <Typography variant="caption" color="text.secondary">
                Access Level, Approved By, and Approved Date are set automatically.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingDelete !== null} onClose={() => !deleting && setPendingDelete(null)}>
        <DialogTitle>Delete access level entry?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{pendingDelete?.userEmail}</strong> from{" "}
            <strong style={{ fontFamily: "monospace" }}>{pendingDelete?.jurisdictionCode}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={onConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
